import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ListPlus } from 'lucide-react'
import { getSetupStandard, seedRetail, setBusinessType } from '../../api/master'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { DEFAULT_LABEL, MasterTabs } from './masterShared'

// Account setup: the standard retail chart of accounts and default
// accounts, what this company already has, and one button that adds only
// what's missing.
export default function AccountSetup() {
  const qc = useQueryClient()
  const { notify } = useToast()
  const { data, isLoading, error: loadError, refetch } = useQuery({ queryKey: ['setup-standard'], queryFn: getSetupStandard })
  const [show, setShow] = useState('missing')
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const [switching, setSwitching] = useState(false)

  const accounts = data?.accounts || []
  const defaults = data?.defaults || []
  const missing = accounts.filter((a) => !a.exists)
  const unset = defaults.filter((d) => !d.current)
  const done = data && !missing.length && !unset.length
  const list = show === 'missing' ? missing : accounts
  const kinds = data?.business_types || {}

  const switchType = async (kind) => {
    if (!kind || kind === data?.business_type) return
    setSwitching(true)
    setError('')
    try {
      await setBusinessType(kind)
      await Promise.all([refetch(), qc.invalidateQueries({ queryKey: ['accounts'] })])
      notify('Equity accounts renamed')
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Couldn’t change the business type.')
    } finally {
      setSwitching(false)
    }
  }

  const fill = async () => {
    setWorking(true)
    setError('')
    try {
      const res = await seedRetail()
      const added = res?.created?.length || 0
      const mapped = Object.keys(res?.mapped || {}).length
      await Promise.all([refetch(), qc.invalidateQueries({ queryKey: ['accounts'] }), qc.invalidateQueries({ queryKey: ['control-accounts'] })])
      notify(added || mapped ? `Added ${added} ${added === 1 ? 'account' : 'accounts'} and set ${mapped} ${mapped === 1 ? 'default' : 'defaults'}` : 'Nothing was missing')
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Couldn’t add the accounts.')
    } finally {
      setWorking(false)
    }
  }

  return (
    <div className="ci-page">
      <MasterTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Account setup <HelpTip text="ASYS Retail writes the journal entries for your sales, purchases and stock. To do that it needs a chart of accounts and a default account for each kind of transaction. This adds the standard ones you don’t have yet." /></h1>
            <div className="ci-title-meta">The accounts the app needs to post your sales, purchases and stock.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          {data && !done && <button type="button" className="ci-btn ci-btn-primary" disabled={working} onClick={fill}><ListPlus size={15} />{working ? 'Adding…' : 'Add what’s missing'}</button>}
        </div>
      </div>

      {(error || loadError) && <ErrorBanner message={error || 'Couldn’t load the standard accounts.'} />}
      {isLoading ? <Spinner label="Checking your accounts…" /> : data && (
        <>
          <div className="mf-setup-status">
            {done ? <StatusBadge label="All set" tone="good" /> : null}
            <span><strong>{accounts.length - missing.length} of {accounts.length}</strong> standard accounts in place</span>
            <span><strong>{defaults.length - unset.length} of {defaults.length}</strong> defaults set</span>
          </div>
          <div className="mf-setup-status">
            <label htmlFor="setup-business-type"><strong>Who owns the business</strong> <HelpTip text="Names the owner’s accounts in equity: Owner’s capital and drawings for a sole owner or partnership; Share capital, paid-in capital and dividends for a company. It can be changed until something is posted to them." /></label>
            <select id="setup-business-type" value={data.business_type || 'company'} disabled={switching} onChange={(e) => switchType(e.target.value)}>
              {Object.entries(kinds).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
          <p className="doc-hint mf-setup-note">
            {done
              ? <>Everything is in place. Add your own accounts in the <Link to="/master/accounts">chart of accounts</Link> and choose the accounts each transaction uses under <Link to="/master/control-accounts">transaction accounts</Link>.</>
              : 'Adding what’s missing keeps your own accounts, names and default choices: it only fills the gaps.'}
          </p>

          <FilterChips options={[{ id: 'missing', label: 'Missing', count: missing.length }, { id: 'all', label: 'All standard accounts', count: accounts.length }]} value={show} onChange={setShow} />
          <div className="ci-table-wrap">
            <div className="ci-table-scroll">
              <table className="ci-table fit-sm">
                <thead><tr><th>Account</th><th className="hide-sm">Type</th><th className="center">Status</th></tr></thead>
                <tbody>
                  {list.length === 0 ? (
                    <tr><td colSpan="3"><div className="ci-empty">All the standard accounts are in place.</div></td></tr>
                  ) : list.map((a) => (
                    <tr key={a.code}>
                      <td className={a.parent ? 'mf-acct-sub' : ''}><span className="mf-acct-code">{a.code}</span>{a.name}</td>
                      <td className="hide-sm ci-muted-cell">{a.type}</td>
                      <td className="center">{a.exists ? <StatusBadge label="In place" tone="good" /> : <StatusBadge label="Missing" tone="warn" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {unset.length > 0 && (
            <div className="ci-table-wrap mf-default-group">
              <h2 className="mf-default-title">Defaults not set</h2>
              <ul className="mf-default-list">
                {unset.map((d) => (
                  <li key={d.name}><span className="mf-default-label">{DEFAULT_LABEL[d.name] || d.name}</span><span className="ci-muted-cell">Standard: {d.code}</span><span /></li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  )
}
